/**
 * What happens when the GRADING step finishes — all of it, in one file.
 *
 * THREE PRODUCERS SHARE THIS: `grading` (one main task × submission), `gradingConsistency` (one main task) and
 * `languageUseGroupGrading` (one submission × language-use scheme group). They are the three PHASES of a grading wave, all
 * three write grades, and they are one step for exactly that reason — see `EXTRA_STEP_TYPES`.
 *
 * WHY IT HAD TO MOVE OUT OF `grading.job.ts`. `job-hooks.ts` runs the completion declared by the TERMINATING ROW's
 * own definition, so whichever of the three finishes last is the one whose declaration is used. With this living on
 * `grading` alone, a wave whose last row was a consistency or language-use-group row found no declaration, closed its
 * batch quietly, and never announced. That is the same trap the two solution steps are in, and the same escape:
 * one object, imported by every producer, with `validateStepCompletionWiring` checking at boot that all three point
 * at it.
 */

import type { FinishedRun, StepCompletion } from '../step.js';
import { finishGradingRun } from '../../agents/gradingAgent.js';

/**
 * Marks the exam graded once the LAST `grading` row of the exam is terminal.
 *
 * The refcount already handles the SESSION — every task-job's `runGradingAgent` call increments
 * `isCurrentlyGrading` and `closeGradingSession` acts at zero — but a refcount cannot see the QUEUE. With three
 * task-jobs and one free runner slot, the first job finishes, the count reaches zero, and the exam is marked
 * COMPLETED while two rows are still pending; the next job then reopens it and the bar goes backwards. So
 * `closeGradingSession` defers the terminal write while any grading row is live, and this fires it at the one
 * moment it is not.
 *
 * ONE PREDICATE, NO RUN. This used to compare a `batchProgress` aggregation over the run's `batchKey` against a
 * count carried on the event. A re-grade dispatched mid-run JOINED the key, which is what made the batch safe —
 * and that is precisely the same thing as "the exam still has a live grading row", so the key was a label on the
 * answer rather than a source of it. A re-grade queued while this drains keeps the predicate false by existing.
 *
 * GRADE AND REGRADE COUNT THE SAME, deliberately: both are grading work on this exam, and "graded" means none of
 * it is outstanding.
 *
 * IT ALSO ANNOUNCES, because this is the one moment per wave at which everything that was re-graded is known — and
 * `run.batch` is now what that means: every trigger's ask, recorded on the run's batch, plus every row's own account
 * of what it graded. The exam-level ledger this replaces had to be drained, and every bug it had was a property of
 * draining; see the header of `pipeline/regrade.ts`. The drainer before THAT held a claim and a lease purely to
 * manufacture the moment this predicate already identifies.
 *
 * A FIRST GRADING STAYS SILENT here by construction: the grading tool appends no cause, so the ledger comes back
 * empty and the tool announces its own result as it always did.
 */
export const closeGradingAndAnnounce = async (run: FinishedRun): Promise<void> => {
  const { examId } = run;
  await finishGradingRun(examId);

  // READ OFF THE WAVE, not off exam state: `run.batch` holds every trigger's ask and every row's account of what it
  // actually graded. No clear and no claim — the right to announce was taken atomically when the batch was closed, so
  // this is a pure read. Dynamic import, because `regrade.ts` imports this module for its payload and scope types.
  const { ledgerFromBatch } = await import('../../pipeline/regrade.js');
  const ledger = ledgerFromBatch(run.batch);
  if (!ledger.causes.length) return;

  const { announceRegradeFinished } = await import('../../pending-tasks/announce-regrade.js');
  await announceRegradeFinished({
    examId,
    causes: ledger.causes,
    // Absent rather than empty in both cases, because `announceRegradeFinished` reads absence as "everything":
    // every submission of the exam, every task of the exam. An empty array would name nothing.
    ...(ledger.submissionIds.length ? { submissionIds: ledger.submissionIds } : {}),
    ...(ledger.taskRefs.length ? { taskNumbers: ledger.taskRefs } : {}),
    ...(typeof ledger.previouslyApprovedCount === 'number'
      ? { previouslyApprovedCount: ledger.previouslyApprovedCount }
      : {}),
  });
};

/**
 * Closes grading when this exam has no row of the STEP left — not merely no `grading` row.
 *
 * NO `progress`, and that omission is the declaration: `grading` is not a `SetupStep`, keeps no progress record of
 * the kind `updateSetupStep` announces, and closes its own session instead. `finish` is where a step says "this is
 * what my completion means" when it is not a setup step at all — see `StepCompletion`.
 */
export const gradingStep: StepCompletion = { finish: closeGradingAndAnnounce };
